import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { CartContext, type CartContextValue, type CartItem } from './cartStore'

const storageKey = 'taijitsu-shop-cart'
const readInitialCart = (): CartItem[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || '[]')
    if (!Array.isArray(parsed)) return []
    return parsed.filter((item) => Number.isInteger(item?.variantId) && Number.isInteger(item?.quantity) && item.quantity > 0)
  } catch {
    return []
  }
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(readInitialCart)

  const commit = useCallback((updater: (current: CartItem[]) => CartItem[]) => {
    setItems((current) => {
      const next = updater(current)
      localStorage.setItem(storageKey, JSON.stringify(next))
      return next
    })
  }, [])

  const value = useMemo<CartContextValue>(() => ({
    items,
    itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
    addItem: (variantId, quantity = 1) => commit((current) => {
      const existing = current.find((item) => item.variantId === variantId)
      return existing
        ? current.map((item) => item.variantId === variantId ? { ...item, quantity: Math.min(99, item.quantity + quantity) } : item)
        : [...current, { variantId, quantity }]
    }),
    setQuantity: (variantId, quantity) => commit((current) => quantity <= 0
      ? current.filter((item) => item.variantId !== variantId)
      : current.map((item) => item.variantId === variantId ? { ...item, quantity: Math.min(99, quantity) } : item)),
    removeItem: (variantId) => commit((current) => current.filter((item) => item.variantId !== variantId)),
    clearCart: () => commit(() => []),
  }), [commit, items])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}
